import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import suppressResizeObserverError from './utils/resizeObserverErrorHandler';
import ErrorBoundary from './components/ErrorBoundary';

// INTERCEPTOR ULTRA-AGRESIVO PARA RESIZEOBSERVER - DEBE IR ANTES QUE TODO
(function() {
  // Interceptar inmediatamente al cargar
  const originalError = console.error;
  const originalWarn = console.warn;
  
  console.error = function(...args) {
    const message = args[0];
    if (
      (typeof message === 'string' && message.includes('ResizeObserver')) ||
      (message && typeof message === 'object' && message.message && message.message.includes('ResizeObserver')) ||
      (message && typeof message === 'object' && message.stack && message.stack.includes('ResizeObserver'))
    ) {
      return; // Completamente ignorar
    }
    originalError.apply(console, args);
  };
  
  console.warn = function(...args) {
    const message = args[0];
    if (typeof message === 'string' && message.includes('ResizeObserver')) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Interceptar errores globales INMEDIATAMENTE
  window.addEventListener('error', function(e) {
    if (
      (e.message && e.message.includes('ResizeObserver')) ||
      (e.error && e.error.message && e.error.message.includes('ResizeObserver')) ||
      (e.filename && e.filename.includes('bundle.js') && (e.lineno === 191540 || e.lineno === 191559)) ||
      (e.filename && e.filename.includes('static/js/bundle.js'))
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true); // true = capture phase
  
  // Interceptor específico para el handleError de React/Webpack
  const originalHandleError = window.console.error;
  Object.defineProperty(window, 'handleError', {
    value: function(...args: any[]) {
      // Si es el error específico de ResizeObserver, no hacer nada
      return;
    },
    writable: true,
    configurable: true
  });

  // Interceptar unhandled rejections
  window.addEventListener('unhandledrejection', function(e) {
    if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
      e.preventDefault();
      return false;
    }
  }, true);

  // Override del ResizeObserver global ANTES de que cualquier componente lo use
  if (window.ResizeObserver) {
    const OriginalResizeObserver = window.ResizeObserver;
    window.ResizeObserver = class extends OriginalResizeObserver {
      constructor(callback: ResizeObserverCallback) {
        super((entries, observer) => {
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error: any) {
              // Silenciar cualquier error de ResizeObserver
              if (error && error.message && error.message.includes('ResizeObserver')) {
                return;
              }
              throw error;
            }
          });
        });
      }
    };
  }
})();

// Suprimir errores no críticos de ResizeObserver
suppressResizeObserverError();

// Interceptor específico para el error de handleError de ResizeObserver
if (typeof window !== 'undefined') {
  // Interceptar específicamente el error que aparece en tu consola
  const originalConsoleError = console.error;
  console.error = function(...args) {
    // Filtrar el error específico de ResizeObserver del bundle
    const firstArg = args[0];
    if (
      (typeof firstArg === 'string' && firstArg.includes('ResizeObserver loop completed with undelivered notifications')) ||
      (firstArg && typeof firstArg === 'object' && firstArg.message && firstArg.message.includes('ResizeObserver')) ||
      (firstArg && typeof firstArg === 'object' && firstArg.stack && firstArg.stack.includes('handleError'))
    ) {
      // Silenciar este error específico
      return;
    }
    
    // Llamar al console.error original para otros errores
    originalConsoleError.apply(console, args);
  };
}

// Método adicional para suprimir ResizeObserver errors
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Override para ResizeObserver más robusto
if (typeof window !== 'undefined') {
  const handleResizeObserverError = debounce((e: any) => {
    if (e.message && e.message.includes('ResizeObserver')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, 100);

  // Capturar errores de error handler
  window.addEventListener('error', (e) => {
    if (
      e.error?.message?.includes('ResizeObserver') ||
      e.message?.includes('ResizeObserver') ||
      e.filename?.includes('handleError')
    ) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  });

  window.addEventListener('error', handleResizeObserverError);
  
  // Capturar errores específicos del bundle
  const originalWindowError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    if (
      (typeof message === 'string' && message.includes('ResizeObserver')) ||
      (source && source.includes('handleError')) ||
      (error && error.message && error.message.includes('ResizeObserver'))
    ) {
      return true; // Prevenir que se muestre el error
    }
    
    if (originalWindowError) {
      return originalWindowError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
